import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import Panel from 'utils/panel';
import clsx from 'clsx';
import Icon from 'utils/icon';
import ProductSpecs from './product-specs';

const ProductDetailsPanel = ({ openTriggerElArray, ...props }) => {
  const [triggers, setTriggers] = useState({});
  const [tray, setTray] = useState('');
  const [currentVariant, setCurrentVariant] =
    useState(props.product.variants.find(
      (v) =>
        JSON.stringify(v.selectedOptions) ===
        JSON.stringify(props.product.variantBySelectedOptions.selectedOptions),
    ));
  const closeTriggerEl = useRef(null);
  const lastTriggerEl = useRef(null);
  const [tabIndex, setTabindex] = useState('-1');
  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setTriggers({
      closeTriggerEl: closeTriggerEl.current,
      openTriggerElArray
    });
  }, [closeTriggerEl, openTriggerElArray]);



  const handleCurrentVariantUpdated = (e) => {
    setTabindex('-1')
    const { currentProductData } = e.detail;
    setCurrentVariant(currentProductData.variants.find(
      (v) =>
        JSON.stringify(v.id) ===
        JSON.stringify(e.detail.currentVariant),
    ));
  };

  const handleExpandTray = (e) => {
    setTray(e.detail.tray);
    setTabindex('0')
  };

  useEffect(() => {
    document.addEventListener('expand-tray', handleExpandTray);
    document.addEventListener('currentVariantUpdated', handleCurrentVariantUpdated);
    return () => {
      window.removeEventListener('expand-tray', handleExpandTray);
      window.removeEventListener('currentVariantUpdated', handleCurrentVariantUpdated);
    };
  }, []);

  const detailsRef = useRef(null);
    useEffect(() => {
      if (tray === 'details' && detailsRef.current) {
      detailsRef.current.focus();
    }
  }, [tray]);

  const closePanel = (e) => {
    if(e.key === 'Enter' ||
    e.key === ' ') {
      setTabindex('-1')
    }
  };

  const focusHandle = (focusedItem, e) => {
    if (!e.shiftKey && e.key === 'Tab') {
      e.preventDefault();
      focusedItem.focus();
    } 
  };

  const backFocusHandle = (focusedItem, e) => {
    if (e.shiftKey && e.key === 'Tab') {
      e.preventDefault();
      focusedItem.focus();
    } 
  };

  return (
    <Panel overlay={true} overlayId='details-overlay' triggers={triggers} defaultOpen={false} context="details" additionalClasses="!w-[26.25rem] max-w-full" tabIndex={tabIndex}>
      <div className='h-[100dvh] flex flex-col' data-target='details-panel' data-panel-detail>
        <div>
          <div className="flex items-center justify-end px-5 py-[18px] border-b border-grey-3 lg:py-3.5">
            <button type="button" ref={closeTriggerEl} 
            tabIndex={tabIndex}
            data-detail-close
            onClick={()=>{setTabindex('-1')}}
            onKeyDown={(e)=>{
              backFocusHandle(lastTriggerEl.current,e);
              closePanel(e)}}
            >
              <Icon name="close" />
              <span className="sr-only">
                {window.TMW.Strings.cart.close}
              </span>
            </button>
          </div>
          <div className="border-b border-grey-3 px-5 flex gap-x-[1.875rem]" role="tablist" aria-label="Product Details Tabs">
            <button
              className={clsx('uppercase pt-[0.9375rem] pb-[0.8125rem] -mb-[1px] utility-3 border-b cursor-pointer', {
                'text-primary border-b-black': tray === 'details',
                'text-grey-10 border-b-transparent': tray !== 'details',
              })}
              type="button"
              role="tab"
              aria-selected={tray === 'details' ? 'true' : 'false'}
              aria-controls="details-panel"
              id="tab-details"
              onClick={() => setTray('details')}
              tabIndex={tabIndex}
            >
              {props.detailsTrayDetailsTitle}
            </button>
            <button className={clsx('uppercase pt-[0.9375rem] pb-[0.8125rem] -mb-[1px] utility-3 border-b cursor-pointer', {
              'text-primary border-b-black': tray === 'dimensions',
              'text-grey-10 border-b-transparent': tray !== 'dimensions',
            })}
              type="button"
              role="tab"
              aria-selected={tray === 'dimensions'}
              aria-controls="dimensions-panel"
              id="tab-dimensions"
              onClick={() => setTray('dimensions')}
              tabIndex={tabIndex}
            >
              {props.detailsTrayDimensionsTitle}
            </button>
            <button className={clsx('uppercase pt-[0.9375rem] pb-[0.8125rem] -mb-[1px] utility-3 border-b cursor-pointer', {
              'text-primary border-b-black': tray === 'shipping',
              'text-grey-10 border-b-transparent': tray !== 'shipping',
            })}
              type="button"
              role="tab"
              aria-selected={tray === 'shipping'}
              aria-controls="shipping-panel"
              id="tab-shipping"
              onClick={() => setTray('shipping')}
              tabIndex={tabIndex}
              ref={lastTriggerEl}
              onKeyDown={(e) =>{focusHandle(closeTriggerEl.current,e)}}
            >
              {props.trayShippingTitle}
            </button>
          </div>
        </div>

        <div className='px-5 py-8 flex-grow overflow-auto'
          ref={detailsRef}
          id="details-panel"
          role="tabpanel"
          aria-labelledby="tab-details"
          aria-hidden={tray !== 'details'}
          aria-live="polite"
          >
          { tray === 'details' && (
            <div>
              <div className='rte' dangerouslySetInnerHTML={{
                __html:
                  currentVariant.detailsTray.detailsBody1
              }} />
              <div 
              className='text-grey-8 paragraph-5 pt-[30px]' dangerouslySetInnerHTML={{
                __html:
                  currentVariant.detailsTray.detailsBody2
              }} />
            </div>
          )}
          <div
            id="dimensions-panel"
            role="tabpanel"
            aria-labelledby="tab-dimensions"
            aria-hidden={tray !== 'dimensions'}
            className="px-5 py-8 flex-grow overflow-auto"
          ></div>
          {tray === 'dimensions' && (
            <ProductSpecs
              currentInputVariant={currentVariant}
              fromForm={false}
              {...props}
            />
          )}
          <div
            id="shipping-panel"
            role="tabpanel"
            aria-labelledby="tab-shipping"
            hidden={tray !== 'shipping'}
            className="px-5 py-8 flex-grow overflow-auto"
          ></div>
          {tray === 'shipping' && (
            <div className='rte' dangerouslySetInnerHTML={{ __html: props.trayShippingBody }} />
          )}
        </div>
      </div>
    </Panel>
  );
};

export default ProductDetailsPanel;
